import { ReportingLanguage } from '../../../../i18n'
import { ReportingAccessLevels } from '../../../NonReact/constants/Permissions'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { IsStringEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { OptionGroup, StandardOptionGroup } from '../../Common/Components/Selects/GroupedSelect/GroupedSelect'
import { SelectItem } from '../../Common/Components/Selects/Types'
import { SERVER_NONE_VALUE } from '../Constants'
import { LicenceResponseMapped } from '../../../NonReact/Types/Types'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { Capabilities } from '../Types'

export interface ReportingLevelSelect extends SelectItem {
    otherLevels: ReportingAccessLevels[]
}

const LEGACY_ACCESS_LEVELS: string[] = [
    ReportingAccessLevels.NONE,
    ReportingAccessLevels.BLF,
    ReportingAccessLevels.ONE,
    ReportingAccessLevels.LITE,
    ReportingAccessLevels.ONE_THOUSAND,
    ReportingAccessLevels.TWO_THOUSAND
]

const ENTERPRISE_ACCESS_LEVELS: string[] = [
    ReportingAccessLevels.ESSENTIALS,
    ReportingAccessLevels.PROFESSIONAL,
    ReportingAccessLevels.ENTERPRISE,
    ReportingAccessLevels.CALL_CENTRE_AGENT,
    ReportingAccessLevels.CALL_CENTRE_SUPERVISOR
]

const ALL_REPORTING_ACCESS_LEVELS_IN_ORDER: ReportingAccessLevels[] = [
    ReportingAccessLevels.NONE,
    ReportingAccessLevels.ESSENTIALS,
    ReportingAccessLevels.PROFESSIONAL,
    ReportingAccessLevels.ENTERPRISE,
    ReportingAccessLevels.CALL_CENTRE_AGENT,
    ReportingAccessLevels.CALL_CENTRE_SUPERVISOR,
    ReportingAccessLevels.BLF,
    ReportingAccessLevels.ONE,
    ReportingAccessLevels.LITE,
    ReportingAccessLevels.ONE_THOUSAND,
    ReportingAccessLevels.TWO_THOUSAND
]

/**
 * Reporting Access Level - create a list of reporting access level options and filter by a given level.
 */
export default class ReportingAccessLevel {
    private language: CLanguageHandler

    constructor(language: CLanguageHandler) {
        this.language = language
    }

    /**
     * Compares current reporting level with max reporting level and returns lesser of two.
     * @param currentLevel user's reporting level
     * @param maxLevel maximum reporting level allowed by partition/telsys
     * @returns current level or max level whichever is lesser
     */
    public getTopLevelOption = (currentLevel: string, maxLevel: string): string => {
        const availableOptions: string[] =
            maxLevel === ReportingAccessLevels.ENTERPRISE
                ? ENTERPRISE_ACCESS_LEVELS
                : LEGACY_ACCESS_LEVELS.slice(
                      0,
                      LEGACY_ACCESS_LEVELS.findIndex((accessLevel: string): boolean => accessLevel === maxLevel)
                  )
        const isCurrentLevelAvailable: boolean = availableOptions.includes(currentLevel)

        return isCurrentLevelAvailable ? currentLevel : maxLevel
    }

    /**
     * Filter options by a given level.
     *
     * @param options - The options to filter.
     * @param level - The level to filter by.
     *
     * @returns - A list of filtered options.
     */
    private filterOptions = (options: ReportingLevelSelect[], level?: ReportingAccessLevels): ReportingLevelSelect[] =>
        !IsStringEmpty(level)
            ? options.filter((personalOption: ReportingLevelSelect): boolean =>
                  personalOption.otherLevels.includes(level)
              )
            : options

    /**
     * The no reporting options.
     *
     * @returns A list of no reporting options.
     */
    private noReporting = (): StandardOptionGroup => ({
        groupTitle: this.language.get(ReportingLanguage.ReportingLevelTypesNone),
        options: [
            {
                label: this.language.get(ReportingLanguage.AccessLevelNone),
                value: SERVER_NONE_VALUE
            }
        ]
    })

    /**
     * Personal reporting options.
     *
     * @param level - The level to filter by.
     *
     * @returns A of personal reporting options filtered by level.
     */
    private personalReporting = (
        level: ReportingAccessLevels = ReportingAccessLevels.TWO_THOUSAND
    ): Nullable<OptionGroup<ReportingLevelSelect>> => {
        const personalOptions: ReportingLevelSelect[] = [
            {
                label: this.language.get(ReportingLanguage.AccessLevelPresence),
                value: ReportingAccessLevels.BLF,
                otherLevels: [
                    ReportingAccessLevels.BLF,
                    ReportingAccessLevels.ONE,
                    ReportingAccessLevels.LITE,
                    ReportingAccessLevels.ONE_THOUSAND,
                    ReportingAccessLevels.TWO_THOUSAND
                ]
            },
            {
                label: this.language.get(ReportingLanguage.AccessLevelOne),
                value: ReportingAccessLevels.ONE,
                otherLevels: [
                    ReportingAccessLevels.ONE,
                    ReportingAccessLevels.LITE,
                    ReportingAccessLevels.ENTERPRISE,
                    ReportingAccessLevels.CALL_CENTRE_SUPERVISOR,
                    ReportingAccessLevels.ONE_THOUSAND,
                    ReportingAccessLevels.TWO_THOUSAND
                ]
            }
        ]

        const filteredPersonalOptions: ReportingLevelSelect[] = this.filterOptions(personalOptions, level)

        return filteredPersonalOptions.length > 0
            ? {
                  groupTitle: this.language.get(ReportingLanguage.ReportingLevelTypesPersonal),
                  options: filteredPersonalOptions
              }
            : undefined
    }

    /**
     * Enterprise reporting options.
     *
     * @param licenceSummary - The licence summary.
     * @param telSysType - The Tel servers type.
     * @param isTelSysHistoricOnly - Is the TelSys historic only.
     *
     * @returns - A list of Enterprise reporting options.
     */
    private enterpriseReporting = (
        licenceSummary: Nullable<LicenceResponseMapped>,
        telSysType: Nullable<TelSysTypes>,
        isTelSysHistoricOnly: Nullable<boolean>
    ): Nullable<OptionGroup<ReportingLevelSelect>> => ({
        groupTitle: this.language.get(ReportingLanguage.ReportingLevelTypesEnterprise),
        options: [
            ...((licenceSummary?.AvailableLicences?.Essentials ?? 1) <= 0 ||
            (isVarNotEmpty(isTelSysHistoricOnly) && !isTelSysHistoricOnly)
                ? []
                : [
                      {
                          label: this.language.getWithMSTCheck(ReportingLanguage.AccessLevelEssentials, telSysType),
                          value: ReportingAccessLevels.ESSENTIALS,
                          otherLevels: [
                              ReportingAccessLevels.ESSENTIALS,
                              ReportingAccessLevels.PROFESSIONAL,
                              ReportingAccessLevels.ENTERPRISE,
                              ReportingAccessLevels.CALL_CENTRE_AGENT,
                              ReportingAccessLevels.CALL_CENTRE_SUPERVISOR
                          ]
                      }
                  ]),
            ...((licenceSummary?.AvailableLicences?.Professional ?? 1) <= 0 || telSysType === TelSysTypes.MS_TEAMS
                ? []
                : [
                      {
                          label: this.language.get(ReportingLanguage.AccessLevelProfessional),
                          value: ReportingAccessLevels.PROFESSIONAL,
                          otherLevels: [
                              ReportingAccessLevels.ESSENTIALS,
                              ReportingAccessLevels.PROFESSIONAL,
                              ReportingAccessLevels.ENTERPRISE,
                              ReportingAccessLevels.CALL_CENTRE_AGENT,
                              ReportingAccessLevels.CALL_CENTRE_SUPERVISOR
                          ]
                      }
                  ]),
            ...((licenceSummary?.AvailableLicences?.Enterprise ?? 1) <= 0 ||
            (telSysType === TelSysTypes.MS_TEAMS && !isTelSysHistoricOnly)
                ? []
                : [
                      {
                          label: this.language.getWithMSTCheck(ReportingLanguage.AccessLevelEnterprise, telSysType),
                          value: ReportingAccessLevels.ENTERPRISE,
                          otherLevels: [
                              ReportingAccessLevels.ESSENTIALS,
                              ReportingAccessLevels.PROFESSIONAL,
                              ReportingAccessLevels.ENTERPRISE,
                              ReportingAccessLevels.CALL_CENTRE_AGENT,
                              ReportingAccessLevels.CALL_CENTRE_SUPERVISOR
                          ]
                      }
                  ]),
            ...((licenceSummary?.AvailableLicences?.CallCentreAgent ?? 1) <= 0 || isTelSysHistoricOnly
                ? []
                : [
                      {
                          label: this.language.getWithMSTCheck(
                              ReportingLanguage.AccessLevelCallCentreAgent,
                              telSysType
                          ),
                          value: ReportingAccessLevels.CALL_CENTRE_AGENT,
                          otherLevels: [
                              ReportingAccessLevels.ESSENTIALS,
                              ReportingAccessLevels.PROFESSIONAL,
                              ReportingAccessLevels.ENTERPRISE,
                              ReportingAccessLevels.CALL_CENTRE_AGENT,
                              ReportingAccessLevels.CALL_CENTRE_SUPERVISOR
                          ]
                      }
                  ]),
            ...((licenceSummary?.AvailableLicences?.CallCentreSupervisor ?? 1) <= 0 || isTelSysHistoricOnly
                ? []
                : [
                      {
                          label: this.language.getWithMSTCheck(
                              ReportingLanguage.AccessLevelCallCentreSupervisor,
                              telSysType
                          ),
                          value: ReportingAccessLevels.CALL_CENTRE_SUPERVISOR,
                          otherLevels: [
                              ReportingAccessLevels.ESSENTIALS,
                              ReportingAccessLevels.PROFESSIONAL,
                              ReportingAccessLevels.ENTERPRISE,
                              ReportingAccessLevels.CALL_CENTRE_AGENT,
                              ReportingAccessLevels.CALL_CENTRE_SUPERVISOR
                          ]
                      }
                  ])
        ]
    })

    /**
     * Supervisor reporting options.
     *
     * @param level - The level to filter by.
     *
     * @returns - A list of supervisor reporting options.
     */
    private supervisorReporting = (
        level: ReportingAccessLevels = ReportingAccessLevels.TWO_THOUSAND
    ): Nullable<OptionGroup<ReportingLevelSelect>> => {
        const supervisorOptions: ReportingLevelSelect[] = [
            {
                label: this.language.get(ReportingLanguage.AccessLevelLite),
                value: ReportingAccessLevels.LITE,
                otherLevels: [
                    ReportingAccessLevels.LITE,
                    ReportingAccessLevels.ONE_THOUSAND,
                    ReportingAccessLevels.TWO_THOUSAND
                ]
            },
            {
                label: this.language.get(ReportingLanguage.AccessLevel1000),
                value: ReportingAccessLevels.ONE_THOUSAND,
                otherLevels: [ReportingAccessLevels.ONE_THOUSAND, ReportingAccessLevels.TWO_THOUSAND]
            },
            {
                label: this.language.get(ReportingLanguage.AccessLevel2000),
                value: ReportingAccessLevels.TWO_THOUSAND,
                otherLevels: [ReportingAccessLevels.TWO_THOUSAND]
            }
        ]

        const filteredSupervisorOptions: ReportingLevelSelect[] = this.filterOptions(supervisorOptions, level)

        return filteredSupervisorOptions.length > 0
            ? {
                  groupTitle: this.language.get(ReportingLanguage.ReportingLevelTypesSupervisor),
                  options: filteredSupervisorOptions
              }
            : undefined
    }

    /**
     * Get the highest access level from a list of access levels.
     *
     * @param availableLevels - list of access levels.
     * @returns The highest access level.
     */
    public getHighestReportingLevelFromList = (availableLevels: string[]): string =>
        ALL_REPORTING_ACCESS_LEVELS_IN_ORDER[
            availableLevels.reduce((highestLevel: number, level: string): number => {
                const levelValue: number = ALL_REPORTING_ACCESS_LEVELS_IN_ORDER.findIndex(
                    (accessLevel: string): boolean => accessLevel === level
                )

                return levelValue > highestLevel ? levelValue : highestLevel
            }, 0)
        ]

    /**
     * Get a reporting levels list filtered by level.
     *
     * @param capabilities - The capabilities.
     * @param telSysType - Current Tel servers type.
     * @param isTelSysHistoricOnly - Is the TelSys historic only.
     * @param licenceSummary - The licence summary.
     * @param levels - The levels to filter by.
     *
     * @returns - Get the options group list.
     */
    public getReportingLevels = (
        capabilities: Capabilities,
        licenceSummary: Nullable<LicenceResponseMapped>,
        telSysType: Nullable<TelSysTypes>,
        isTelSysHistoricOnly: Nullable<boolean>,
        levels: ReportingAccessLevels[] = [ReportingAccessLevels.TWO_THOUSAND, ReportingAccessLevels.ENTERPRISE]
    ): Nullable<StandardOptionGroup>[] => {
        const highestLevel: ReportingAccessLevels = this.getHighestReportingLevelFromList(
            levels
        ) as ReportingAccessLevels
        const hasEnterprise: boolean = !!levels?.includes(ReportingAccessLevels.ENTERPRISE) && capabilities.enterprise
        const hasLegacy: boolean =
            !!levels?.some((level: string): boolean => LEGACY_ACCESS_LEVELS.includes(level)) && capabilities.legacy

        return [
            this.noReporting(),
            ...(hasLegacy ? [this.personalReporting(highestLevel)] : []),
            ...(hasEnterprise ? [this.enterpriseReporting(licenceSummary, telSysType, isTelSysHistoricOnly)] : []),
            ...(hasLegacy ? [this.supervisorReporting(highestLevel)] : [])
        ]
    }

    public static newInstance(language: CLanguageHandler): ReportingAccessLevel {
        return new ReportingAccessLevel(language)
    }
}

export { ReportingAccessLevels } from '../../../NonReact/constants/Permissions'
